import React, { useMemo } from 'react';
import type { ReactNode } from 'react';
import classNames from 'classnames';
import { AccordionItemWrapper } from './components/AccordionItemWrapper';
import { ThinkInlineList } from './components/ThinkAccordionItemRenderer';
import { useSwitchAgentAutoExpand } from '@/pages/aigc/pages/Agent/ChatPage/hooks/useSwitchAgentAutoExpand';
import './index.scss';

export interface ISwitchAgentAccordionItem {
  /** 思考过程的唯一标识 */
  thinkId: string;
  /** 项目类型（如：switch_agent） */
  type: string;
  /** 输入索引，用于标识输入顺序 */
  inputIndex?: number;
  /** 是否已完成处理 */
  done?: boolean;
  /** 新的 Agent 名称（用于 switch_agent 类型） */
  new_agent?: string;
  /** 输入内容 */
  input?: string;
  /** 渲染的内容节点 */
  content?: ReactNode;
  /** 其他扩展属性 */
  [key: string]: any;
}

export interface ISwitchAgentAccordionGroup {
  /** 分组的唯一标识 */
  id: string;
  /** 分组头部项（tool_use 映射为 switch_agent 形态，复用原折叠组件） */
  header: ISwitchAgentAccordionItem;
  /** 分组内的子项列表 */
  items: ISwitchAgentAccordionItem[];
  /** 折叠组前正文项（switch_agent / metadata text 等，渲染层处理） */
  inlineBeforeItems?: ISwitchAgentAccordionItem[];
}

interface ISwitchAgentAccordionProps {
  /** 分组数据源，包含 switch_agent 和其他类型的数据 */
  dataSource?: ISwitchAgentAccordionGroup[];
  /** 是否默认展开第一项 */
  defaultOpenFirst?: boolean;
  /** 流式输出状态，用于控制自动展开行为 */
  streamingState: string;
  /** 是否处于整体加载状态 */
  isLoading?: boolean;
  /** 自定义容器类名 */
  className?: string;
  /** 子级内容区域的自定义类名 */
  contentClassName?: string;
  /** 展开状态的图标类型 */
  iconTypeOpen?: string;
  /** 收起状态图标类型 */
  iconTypeClose?: string;
  /** 是否启用吸顶功能，默认 true */
  enableSticky?: boolean;
  /** 滚动容器类名 */
  scrollContainerClassName?: string;
  /**
   * 文案最大显示行数（吸顶 / 非吸顶均生效），超出显示 "..." 并 hover 出气泡展示完整文案
   * 不传 / 0 表示不限制
   * 推荐值：PC=5、Copilot=10、移动端=15
   */
  stickyMaxLines?: number;
  /** 消息 ID，用于思考区 Markdown 渲染 */
  messageId?: string;
  /** 所有折叠组之后的正文项 */
  trailingInlineItems?: ISwitchAgentAccordionItem[];
}

/**
 * SwitchAgentAccordion 手风琴组件
 */
export const SwitchAgentAccordion: React.FC<ISwitchAgentAccordionProps> = ({
  dataSource = [],
  defaultOpenFirst = false,
  streamingState,
  className,
  enableSticky = true,
  scrollContainerClassName,
  trailingInlineItems,
  messageId,
  ...restProps
}) => {
  const flatDataSource = useMemo(
    () => dataSource?.flatMap(({ header, items }) => [header, ...items]) ?? [],
    [dataSource],
  );

  const { expandedIds, handleCollapseGroup, expandStateRef, addToManualList } = useSwitchAgentAutoExpand({
    dataSource: flatDataSource,
    defaultOpenFirst,
    streamingState,
  });

  return (
    <div className={classNames('dba-switch-agent-accordion', className)}>
      {dataSource.map((group, index) => {
        if (!group.header) return null;
        return (
          <React.Fragment key={group.id}>
            {!!group.inlineBeforeItems?.length && (
              <ThinkInlineList
                keyPrefix={`inline-${group.id}`}
                items={group.inlineBeforeItems}
                streamingState={streamingState}
                messageId={messageId}
              />
            )}
            <AccordionItemWrapper
              {...{
                id: group.id,
                group,
                displayIndex: group.header.inputIndex ?? index + 1,
                expandedIds,
                expandStateRef,
                streamingState,
                handleCollapseGroup,
                enableSticky,
                scrollContainerClassName,
                addToManualList,
                messageId,
                ...restProps,
              }}
            />
          </React.Fragment>
        );
      })}
      {!!trailingInlineItems?.length && (
        <ThinkInlineList
          keyPrefix="trailing-inline"
          items={trailingInlineItems}
          streamingState={streamingState}
          messageId={messageId}
        />
      )}
    </div>
  );
};
